import { useEffect, useState } from 'react';
import {
    DialogContent,
    Typography,
    Divider,
    TextField,
    Icon,
    useTheme,
    Grid,
    Box,
    useAppSelector,
    useAppDispatch,
} from '../../base';
import {
    ITrackableSource,
    openShareWindow,
    ShareLink,
    TrackableSourceNames,
} from '../../common';
import { useLocation } from 'react-router-dom';
import { RoutePaths } from '../../routes/RoutePaths';
import PargodaModal from '../PargodaModal';
import {
    BottomSection,
    AddTrackableButton,
    CopyButton,
    DefaultLinkContainer,
    LinkText,
    ShareButton,
    TrackableLinkItem,
    ActionButton,
    ButtonsWrap,
} from './ShareJobModal.styles';
import { useTranslation } from 'react-i18next';
import { jobsActions, jobsSelectors, userSelectors } from '../../actions';

interface Props {
    modalTitle: string;
    isModalOpen: boolean;
    setIsModalOpen: (isModalOpen: boolean) => void;
    jobId?: number;
}

const ShareModal = (props: Props) => {
    const { isModalOpen, setIsModalOpen, modalTitle, jobId } = props;
    const [isAddingLink, setIsAddingLink] = useState(false);
    const [newLinkName, setNewLinkName] = useState('');
    const [trackableLinks, setTrackableLinks] = useState<
        Array<ITrackableSource>
    >([]);
    const location = useLocation();
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const currentJob = useAppSelector((state) =>
        jobsSelectors.getJobById(state, jobId || null)
    );
    const company = useAppSelector(userSelectors.getCompanyInfo);
    const privateTrackableSources = useAppSelector(
        jobsSelectors.getTrackableSources
    );
    const publicTrackableSources = useAppSelector(
        jobsSelectors.getPublicTrackableSources
    );
    const pathnames = location.pathname.split('/').filter((x) => x);
    const isJobPage = `/${pathnames[0]}` === RoutePaths.jobs;
    const trackableSources = isJobPage
        ? privateTrackableSources
        : publicTrackableSources;
    let companyKey = company?.key;
    if (!companyKey) {
        const params = new URLSearchParams(window.location.search);
        companyKey = params.get('ck') ?? '';
    }
    const defaultUrl = jobId
        ? `${window.location.origin}/positions/${jobId}?ck=${companyKey}&ref=${trackableSources['Direct']}`
        : `${window.location.origin}/positions?ck=${companyKey}&ref=${trackableSources['Direct']}`;

    const urlWithoutRef = defaultUrl.split('&ref=')[0];

    const shareButtons = [
        { platform: TrackableSourceNames.FACEBOOK, iconName: 'FacebookIcon' },
        { platform: TrackableSourceNames.TELEGRAM, iconName: 'TelegramIcon' },
        { platform: TrackableSourceNames.X, iconName: 'TwitterIcon' },
        { platform: TrackableSourceNames.WHATSAPP, iconName: 'WhatsappIcon' },
        { platform: TrackableSourceNames.EMAIL, iconName: 'MailIcon' },
        { platform: TrackableSourceNames.LINKEDIN, iconName: 'LinkedInIcon' },
    ];

    useEffect(() => {
        if (trackableSources) {
            const excludedSources = [
                TrackableSourceNames.X,
                TrackableSourceNames.FACEBOOK,
                TrackableSourceNames.LINKEDIN,
                TrackableSourceNames.TELEGRAM,
                TrackableSourceNames.EMAIL,
                TrackableSourceNames.DIRECT,
                TrackableSourceNames.WHATSAPP,
                TrackableSourceNames.SITE,
            ];
            const filteredSources = Object.entries(trackableSources)
                .filter(([key]) => !excludedSources.includes(key))
                .map(([key, value]) => ({
                    name: key,
                    uuid: value,
                }));
            setTrackableLinks(filteredSources);
        }
    }, [trackableSources]);

    const handleCopy = (text: string) => {
        dispatch(jobsActions.createTrackableSource({ name: newLinkName }));
        navigator.clipboard.writeText(text);
    };

    const handleShareUrl = (socialMedia: TrackableSourceNames) => {
        switch (socialMedia) {
            case TrackableSourceNames.FACEBOOK:
                return (
                    urlWithoutRef +
                    `&ref=${trackableSources[TrackableSourceNames.FACEBOOK]}`
                );
            case TrackableSourceNames.LINKEDIN:
                return (
                    urlWithoutRef +
                    `&ref=${trackableSources[TrackableSourceNames.LINKEDIN]}`
                );
            case TrackableSourceNames.X:
                return (
                    urlWithoutRef +
                    `&ref=${trackableSources[TrackableSourceNames.X]}`
                );
            case TrackableSourceNames.TELEGRAM:
                return (
                    urlWithoutRef +
                    `&ref=${trackableSources[TrackableSourceNames.TELEGRAM]}`
                );
            case TrackableSourceNames.WHATSAPP:
                return (
                    urlWithoutRef +
                    `&ref=${trackableSources[TrackableSourceNames.WHATSAPP]}`
                );
            case TrackableSourceNames.EMAIL:
                return (
                    urlWithoutRef +
                    `&ref=${trackableSources[TrackableSourceNames.EMAIL]}`
                );
            default:
                return defaultUrl;
        }
    };

    const handleAddLink = () => {
        dispatch(jobsActions.createTrackableSource({ name: newLinkName }));
        setIsAddingLink(false);
        setNewLinkName('');
    };

    // Format job details for sharing
    const formatJobDetails = (url: string): string => {
        const title = currentJob?.title || t('shareJobModal.jobOpening');
        const description = t('shareJobModal.CheckOutThisJobOpening');

        return `${title}\n\n${description}\n${url}`;
    };

    const renderShareButton = (button: {
        platform: TrackableSourceNames;
        iconName: string;
    }) => {
        const shareUrl = handleShareUrl(button.platform);
        return (
            <ShareButton
                onClick={() =>
                    openShareWindow(
                        button.platform,
                        shareUrl,
                        t('shareJobModal.jobOpening'),
                        t('shareJobModal.CheckOutThisJobOpening'),
                        formatJobDetails(shareUrl)
                    )
                }
                key={button.platform}
            >
                <Icon name={button.iconName} />
            </ShareButton>
        );
    };

    return (
        <PargodaModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            sx={{ width: '550px', maxWidth: '100%' }}
        >
            <DialogContent>
                <Grid container justifyContent="center">
                    <Grid
                        item
                        xs={12}
                        display={'flex'}
                        justifyContent={'center'}
                        sx={{ mb: 3 }}
                    >
                        <Typography
                            variant={'h4'}
                            color={theme.palette.grey[600]}
                        >
                            {modalTitle}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 3 }}>
                        <ButtonsWrap>
                            {shareButtons.map((button) =>
                                renderShareButton(button)
                            )}
                        </ButtonsWrap>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 2,
                                textAlign: 'center',
                                color: theme.palette.grey[600],
                            }}
                        >
                            {t('shareJobModal.shareWithLink')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <DefaultLinkContainer>
                            <Typography variant="body2" sx={{ flex: 1 }}>
                                {defaultUrl}
                            </Typography>
                            <CopyButton onClick={() => handleCopy(defaultUrl)}>
                                <Icon name="CopyIcon" />
                            </CopyButton>
                        </DefaultLinkContainer>
                    </Grid>
                    {isJobPage && (
                        <>
                            <Grid item xs={12}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mb: 2,
                                        fontWeight: 'bold',
                                        color: theme.palette.grey[600],
                                    }}
                                >
                                    {t('shareJobModal.trackableLinks')}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                    }}
                                >
                                    <div>
                                        {trackableLinks.map((link, index) => (
                                            <div key={link.uuid}>
                                                <TrackableLinkItem>
                                                    <LinkText variant="body2">
                                                        {link.name}
                                                    </LinkText>
                                                    <CopyButton
                                                        onClick={() =>
                                                            handleCopy(
                                                                `${urlWithoutRef}&ref=${link.uuid}`
                                                            )
                                                        }
                                                    >
                                                        <Icon name="CopyIcon" />
                                                    </CopyButton>
                                                </TrackableLinkItem>
                                                {index <
                                                    trackableLinks.length -
                                                        1 && <Divider />}
                                            </div>
                                        ))}
                                    </div>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <AddTrackableButton
                                    fullWidth
                                    onClick={() => setIsAddingLink(true)}
                                >
                                    {t('shareJobModal.addTrackableLink')}
                                </AddTrackableButton>
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>

            {isAddingLink && (
                <Box
                    sx={{
                        backgroundColor: 'lightgrey',
                        margin: '0 -24px -24px -24px',
                        borderRadius: '0 0 8px 8px',
                    }}
                >
                    <BottomSection>
                        <Box sx={{ padding: '16px' }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ mb: 1, color: theme.palette.grey[600] }}
                            >
                                {t('shareJobModal.trackableLink')}
                            </Typography>
                            <TextField
                                fullWidth
                                value={newLinkName}
                                onChange={(e) => setNewLinkName(e.target.value)}
                                placeholder={t('shareJobModal.enterLinkName')}
                                sx={{ mb: 2 }}
                            />
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 8,
                                }}
                            >
                                <ActionButton
                                    variant="outlined"
                                    onClick={() => setIsAddingLink(false)}
                                >
                                    {t('shareJobModal.cancel')}
                                </ActionButton>
                                <ActionButton
                                    variant="contained"
                                    onClick={handleAddLink}
                                    sx={{ backgroundColor: 'warning.main' }}
                                >
                                    {t('shareJobModal.done')}
                                </ActionButton>
                            </div>
                        </Box>
                    </BottomSection>
                </Box>
            )}
        </PargodaModal>
    );
};

export default ShareModal;
